import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/common/Layout';
import { useAppData } from '../context/AppDataContext';
import { useAuth } from '../context/AuthContext';
import { DailyCallLogRow, DailyCallLogStatusColumn } from '../types';
import { callReportService } from '../services/callReportService';
import {
  DateRangePreset,
  formatDate,
  getDateRangeForPreset,
  getTodayISO,
} from '../utils/dateUtils';
import { getSettingColorClass } from '../utils/settingColors';

const DailyCallLog: React.FC = () => {
  const { user } = useAuth();
  const { users, refreshUsersForDropdown } = useAppData();
  const [rows, setRows] = useState<DailyCallLogRow[]>([]);
  const [statusColumns, setStatusColumns] = useState<DailyCallLogStatusColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [calledBy, setCalledBy] = useState<number | ''>('');
  const [preset, setPreset] = useState<DateRangePreset>('today');
  const today = getTodayISO();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  useEffect(() => {
    refreshUsersForDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canViewReports =
    user?.role === 'org_admin' || user?.role === 'manager' || user?.role === 'executive';

  const loadReport = async (nextCalledBy: number | '', from: string, to: string) => {
    try {
      setLoading(true);
      setError('');
      const result = await callReportService.getDailyCallLog({
        called_by:
          user?.role === 'executive' && user?.id != null
            ? user.id
            : nextCalledBy === ''
              ? undefined
              : nextCalledBy,
        date_from: from || undefined,
        date_to: to || undefined,
      });
      setRows(result.data || []);
      setStatusColumns(result.status_columns || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewReports) {
      return;
    }
    loadReport(calledBy, dateFrom, dateTo);
    // Initial load and when role/user identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.id]);

  const applyPreset = (next: DateRangePreset) => {
    setPreset(next);
    if (next === 'custom') {
      return;
    }
    const range = getDateRangeForPreset(next);
    setDateFrom(range.from);
    setDateTo(range.to);
    loadReport(calledBy, range.from, range.to);
  };

  const handleSearch = () => {
    loadReport(calledBy, dateFrom, dateTo);
  };

  const handleClear = () => {
    const range = getDateRangeForPreset('today');
    setCalledBy('');
    setPreset('today');
    setDateFrom(range.from);
    setDateTo(range.to);
    loadReport('', range.from, range.to);
  };

  const totals = useMemo(() => {
    const statusTotals: Record<string, number> = {};
    statusColumns.forEach((col) => {
      statusTotals[col.setting_key] = 0;
    });
    const acc = {
      lead_calls: 0,
      tender_calls: 0,
      customer_calls: 0,
      total_calls: 0,
      status_counts: statusTotals,
    };
    rows.forEach((r) => {
      acc.lead_calls += r.lead_calls;
      acc.tender_calls += r.tender_calls;
      acc.customer_calls += r.customer_calls;
      acc.total_calls += r.total_calls;
      statusColumns.forEach((col) => {
        acc.status_counts[col.setting_key] += r.status_counts?.[col.setting_key] ?? 0;
      });
    });
    return acc;
  }, [rows, statusColumns]);

  if (!canViewReports) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
          You do not have permission to view this report.
        </div>
      </Layout>
    );
  }

  const colSpan = 6 + statusColumns.length;

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Daily Call Log</h1>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            {user?.role !== 'executive' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Executive</label>
                <select
                  value={calledBy}
                  onChange={(e) => setCalledBy(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">All Executives</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={preset}
                onChange={(e) => applyPreset(e.target.value as DateRangePreset)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7">Last 7 days</option>
                <option value="last_30">Last 30 days</option>
                <option value="this_month">This month</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {preset === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-bold"
              >
                Search
              </button>
              <button
                onClick={handleClear}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
        )}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Tender
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  {statusColumns.map((col) => (
                    <th
                      key={col.setting_key}
                      className={`px-3 py-2 text-right text-xs font-bold uppercase tracking-wider ${getSettingColorClass(col.color_code)}`}
                    >
                      {col.display_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={colSpan} className="px-3 py-6 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-3 py-6 text-center text-sm text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={`${r.called_by}-${r.call_date}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-xs text-gray-700">{r.called_by_name || '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{formatDate(r.call_date)}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 text-right">{r.lead_calls}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 text-right">{r.tender_calls}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 text-right">{r.customer_calls}</td>
                      <td className="px-3 py-2 text-xs text-gray-900 text-right font-semibold">{r.total_calls}</td>
                      {statusColumns.map((col) => (
                        <td key={col.setting_key} className="px-3 py-2 text-xs text-gray-700 text-right">
                          {r.status_counts?.[col.setting_key] ?? 0}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && rows.length > 0 && (
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-3 py-2 text-xs font-bold text-gray-800" colSpan={2}>
                      Total
                    </td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-800 text-right">{totals.lead_calls}</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-800 text-right">{totals.tender_calls}</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-800 text-right">{totals.customer_calls}</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-900 text-right">{totals.total_calls}</td>
                    {statusColumns.map((col) => (
                      <td key={col.setting_key} className="px-3 py-2 text-xs font-bold text-gray-800 text-right">
                        {totals.status_counts[col.setting_key] ?? 0}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DailyCallLog;
